import { Status, Wrapper } from '@googlemaps/react-wrapper'
import { useEffect, useRef } from 'react'
import { JustSpinner } from 'components/Loading/Loading'
import { OtherText } from 'styling/Typography'
import { colors } from 'styling/GlobalStyles'
import { tokens } from 'styling/tokens'
import { ILocationNode, ILocationsRegistryMapProps } from 'types/locationsRegistry.interface'
import { inPoly, toLatLngPath, TYPE_META } from './locationsMockData'
import { LocationsMapContainer, LocationsMapLoading } from './Locations.styled'

const isFinitePt = (lat: number, lng: number) => Number.isFinite(lat) && Number.isFinite(lng)

const mapOptions: google.maps.MapOptions = {
  gestureHandling: 'greedy',
  zoom: 5,
  mapId: '4504f8b37365c3d0',
  center: { lat: 0, lng: 0 },
  backgroundColor: tokens.surface.base,
  disableDefaultUI: true,
  mapTypeId: 'satellite',
  styles: [
    {
      featureType: 'all',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ],
}

const RegistryMap = ({
  geos,
  geoIds,
  nodeById,
  drawTarget,
  draft,
  ancestor,
  outsideCount,
  colorFor,
  onMapClick,
  onMouseMove,
  onGeoHover,
  onGeoClick,
  selectedGeoId,
  focusAreaId,
}: ILocationsRegistryMapProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const overlaysRef = useRef<google.maps.MVCObject[]>([])
  const listenersRef = useRef<google.maps.MapsEventListener[]>([])
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null)
  const focusedAreaRef = useRef<number | null>(null)
  const drawTargetRef = useRef(drawTarget)
  const onMapClickRef = useRef(onMapClick)
  const onMouseMoveRef = useRef(onMouseMove)
  const onGeoHoverRef = useRef(onGeoHover)
  const onGeoClickRef = useRef(onGeoClick)
  const colorForRef = useRef(colorFor)
  const nodeByIdRef = useRef(nodeById)
  const ancestorRef = useRef(ancestor)
  const selectedGeoIdRef = useRef(selectedGeoId)

  drawTargetRef.current = drawTarget
  onMapClickRef.current = onMapClick
  onMouseMoveRef.current = onMouseMove
  onGeoHoverRef.current = onGeoHover
  onGeoClickRef.current = onGeoClick
  colorForRef.current = colorFor
  nodeByIdRef.current = nodeById
  ancestorRef.current = ancestor
  selectedGeoIdRef.current = selectedGeoId

  const clearOverlays = () => {
    overlaysRef.current.forEach((overlay) => {
      if ('setMap' in overlay && typeof overlay.setMap === 'function') {
        overlay.setMap(null)
      }
    })
    overlaysRef.current = []
  }

  const clearListeners = () => {
    listenersRef.current.forEach((listener) => listener.remove())
    listenersRef.current = []
  }

  useEffect(() => {
    if (!ref.current || mapRef.current) return
    mapRef.current = new google.maps.Map(ref.current, mapOptions)
    infoWindowRef.current = new google.maps.InfoWindow({ disableAutoPan: true, headerDisabled: true })
  }, [])

  useEffect(() => {
    if (drawTarget) infoWindowRef.current?.close()
  }, [drawTarget])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    clearListeners()

    const clickListener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (drawTargetRef.current) {
        if (!e.latLng) return
        onMapClickRef.current(e.latLng.lat(), e.latLng.lng())
        return
      }
      onGeoClickRef.current(null)
    })

    const moveListener = map.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
      if (drawTargetRef.current && e.latLng) {
        onMouseMoveRef.current([e.latLng.lat(), e.latLng.lng()])
      }
    })

    const leaveListener = map.addListener('mouseout', () => {
      if (drawTargetRef.current) onMouseMoveRef.current(null)
    })

    listenersRef.current = [clickListener, moveListener, leaveListener]

    return clearListeners
  }, [])

  const ancestorPtsKey = ancestor ? ancestor.pts.map((p) => p.join(',')).join(';') : ''
  const geoIdsKey = geoIds.join(',')

  const polygonStyle = (
    id: number,
    geo: { restricted?: boolean },
    node: ILocationNode | undefined,
  ) => {
    const strokeColor = colorForRef.current(node, geo)
    const isSelected = selectedGeoIdRef.current === id
    return {
      strokeColor,
      strokeOpacity: 1,
      strokeWeight: isSelected ? 3 : geo.restricted ? 2.5 : 1.5,
      fillColor: strokeColor,
      fillOpacity: isSelected ? 0.3 : 0.15,
    }
  }

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    clearOverlays()

    if (focusAreaId && geos[focusAreaId] && focusedAreaRef.current !== focusAreaId) {
      const bounds = new google.maps.LatLngBounds()
      geos[focusAreaId].pts.forEach(([lat, lng]) => {
        if (isFinitePt(lat, lng)) bounds.extend({ lat, lng })
      })
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 48)
        focusedAreaRef.current = focusAreaId
      }
    } else if (!focusedAreaRef.current && geoIds.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      geoIds.forEach((id) => {
        geos[id]?.pts.forEach(([lat, lng]) => {
          if (isFinitePt(lat, lng)) bounds.extend({ lat, lng })
        })
      })
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 48)
        focusedAreaRef.current = -1
      }
    }

    const isInteractive = !drawTarget

    geoIds.forEach((id) => {
      const geo = geos[id]
      const node = nodeByIdRef.current(id)
      if (!geo) return

      const validPts = geo.pts.filter(([lat, lng]) => isFinitePt(lat, lng))
      if (validPts.length === 0) return

      const path = toLatLngPath(validPts)

      if (geo.kind === 'polyline') {
        const polyline = new google.maps.Polyline({
          path,
          map,
          strokeColor: tokens.status.neutral.fg,
          strokeOpacity: 0.9,
          strokeWeight: 4,
          zIndex: 2,
          clickable: isInteractive,
        })
        if (isInteractive && node) {
          polyline.addListener('mouseover', (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return
            infoWindowRef.current?.setContent(
              `<div style="font-size:13px;padding:2px 0"><strong>${node.name}</strong><br/>${TYPE_META[node.type]?.label || node.type}</div>`,
            )
            infoWindowRef.current?.setPosition(e.latLng)
            infoWindowRef.current?.open({ map, shouldFocus: false })
            onGeoHoverRef.current(id)
          })
          polyline.addListener('mouseout', () => {
            infoWindowRef.current?.close()
            onGeoHoverRef.current(null)
          })
          polyline.addListener('click', () => onGeoClickRef.current(id))
        }
        overlaysRef.current.push(polyline)
        return
      }

      if (geo.kind === 'point') {
        const [lat, lng] = validPts[0]
        const marker = new google.maps.Marker({
          position: { lat, lng },
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: colors.orange,
            fillOpacity: 1,
            strokeColor: colors.orange,
            strokeWeight: 2,
          },
          zIndex: 3,
          clickable: isInteractive,
          title: isInteractive ? node?.name : undefined,
        })
        if (isInteractive && node) {
          marker.addListener('mouseover', () => onGeoHoverRef.current(id))
          marker.addListener('mouseout', () => onGeoHoverRef.current(null))
          marker.addListener('click', () => onGeoClickRef.current(id))
        }
        overlaysRef.current.push(marker)
        return
      }

      const style = polygonStyle(id, geo, node)
      const polygon = new google.maps.Polygon({
        paths: path,
        map,
        ...style,
        zIndex: 1,
        clickable: isInteractive,
      })

      if (isInteractive && node) {
        polygon.addListener('mouseover', (e: google.maps.MapMouseEvent) => {
          if (!e.latLng) return
          polygon.setOptions({ fillOpacity: 0.35, strokeWeight: style.strokeWeight + 1 })
          infoWindowRef.current?.setContent(
            `<div style="font-size:13px;padding:2px 0"><strong>${node.name}</strong><br/>${TYPE_META[node.type]?.label || node.type}</div>`,
          )
          infoWindowRef.current?.setPosition(e.latLng)
          infoWindowRef.current?.open({ map, shouldFocus: false })
          onGeoHoverRef.current(id)
        })
        polygon.addListener('mouseout', () => {
          polygon.setOptions(style)
          infoWindowRef.current?.close()
          onGeoHoverRef.current(null)
        })
        polygon.addListener('click', () => onGeoClickRef.current(id))
      }

      overlaysRef.current.push(polygon)
    })

    if (drawTarget && ancestorRef.current) {
      const ancestorPoly = new google.maps.Polygon({
        paths: toLatLngPath(ancestorRef.current.pts),
        map,
        strokeColor: colors.orange,
        strokeOpacity: 0.8,
        strokeWeight: 1.5,
        fillOpacity: 0,
        clickable: false,
        zIndex: 4,
      })
      overlaysRef.current.push(ancestorPoly)
    }

    if (drawTarget && draft.length > 0) {
      const draftPath = toLatLngPath(draft)
      const draftStroke = outsideCount > 0 ? colors.red : colors.orange

      if (draft.length > 1) {
        const draftShape =
          nodeByIdRef.current(drawTarget)?.type === 'ROAD'
            ? new google.maps.Polyline({
                path: draftPath,
                map,
                strokeColor: draftStroke,
                strokeOpacity: 1,
                strokeWeight: 3,
                zIndex: 5,
              })
            : new google.maps.Polygon({
                paths: draftPath,
                map,
                strokeColor: draftStroke,
                strokeOpacity: 1,
                strokeWeight: 2,
                fillColor: colors.orange,
                fillOpacity: 0.15,
                zIndex: 5,
              })
        overlaysRef.current.push(draftShape)
      }

      draft.forEach((pt) => {
        const outside = ancestorRef.current ? !inPoly(pt, ancestorRef.current.pts) : false
        const marker = new google.maps.Marker({
          position: { lat: pt[0], lng: pt[1] },
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor: outside ? colors.red : colors.orange,
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 1,
          },
          zIndex: 6,
        })
        overlaysRef.current.push(marker)
      })
    }

    return clearOverlays
  }, [geos, geoIdsKey, drawTarget, draft, ancestorPtsKey, outsideCount, focusAreaId, selectedGeoId])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !drawTarget || !draft.length) return
    const last = draft[draft.length - 1]
    if (!last || !isFinitePt(last[0], last[1])) return
    if (draft.length === 1) {
      map.panTo({ lat: last[0], lng: last[1] })
      if ((map.getZoom() || 0) < 14) map.setZoom(14)
      return
    }
    const bounds = new google.maps.LatLngBounds()
    draft.forEach(([lat, lng]) => {
      if (isFinitePt(lat, lng)) bounds.extend({ lat, lng })
    })
    if (!bounds.isEmpty()) map.fitBounds(bounds, 64)
  }, [drawTarget, draft.length, draft[draft.length - 1]?.[0], draft[draft.length - 1]?.[1]])

  return <LocationsMapContainer ref={ref} />
}

export const LocationsRegistryMap = (props: ILocationsRegistryMapProps) => (
  <Wrapper
    apiKey={process.env.REACT_APP_GOOGLE_KEY}
    version='beta'
    libraries={['marker']}
    render={(status) =>
      status === Status.SUCCESS ? (
        <RegistryMap {...props} />
      ) : (
        <LocationsMapLoading>
          {status === Status.FAILURE ? (
            <OtherText grey>Map failed to load</OtherText>
          ) : (
            <JustSpinner />
          )}
        </LocationsMapLoading>
      )
    }
  />
)
